import { useState } from 'react';
import { useClusterApi } from '../../api/ClusterApiProvider';
import type { Resource } from '../../api/types';
import type { ApiError } from '../../api/errors';
import { useI18n } from '../../i18n/useI18n';
import { Alert, ErrorAlert } from '../../ui/Alert';
import { AttributionNotice } from '../../ui/AttributionNotice';
import { Button } from '../../ui/primitives';
import { Modal } from '../../ui/Modal';
import { removeResource } from './catalogWrite';
import type { WriteOutcome } from './catalogWrite';

/**
 * Removing a resource from the catalogue, which is how access to it is closed.
 *
 * Worth confirming for that reason and no other: a request naming a resource
 * the cluster's catalogue does not hold is refused before policy is consulted,
 * so this is not a tidy-up of a list — it is a door shut on everyone at once.
 * The dialog says so, and says the one thing about it that is easy to get
 * wrong: until the removal reaches the cluster, the cluster still serves it.
 */
export function RemoveResourceDialog({
  resource,
  actorName,
  onClose,
  onRemoved,
}: {
  resource: Resource;
  actorName: string;
  onClose: () => void;
  onRemoved: (id: string, outcome: WriteOutcome) => void;
}) {
  const { t } = useI18n();
  const api = useClusterApi();

  const [failure, setFailure] = useState<ApiError | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const id = resource.id ?? '';

  async function remove() {
    setFailure(null);
    setSubmitting(true);
    const outcome = await removeResource(api, id);
    setSubmitting(false);

    if (outcome.done === 'refused') {
      setFailure(outcome.error);
      return;
    }
    onRemoved(id, outcome);
  }

  return (
    <Modal
      title={t('resource.remove.title')}
      subtitle={t('resource.remove.subtitle', { id })}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose} disabled={submitting}>
            {t('common.cancel')}
          </Button>
          <span className="spacer" />
          <Button variant="danger" disabled={submitting} onClick={() => void remove()}>
            {submitting ? `${t('resource.remove.submitting')}…` : t('resource.remove.submit')}
          </Button>
        </>
      }
    >
      <div className="stack">
        {failure !== null && <RemoveFailure failure={failure} />}

        <Alert tone="warn" title={t('resource.remove.effect.title')}>
          <p>{t('resource.remove.effect.body')}</p>
          <p style={{ marginTop: 8 }}>{t('resource.remove.effect.until')}</p>
        </Alert>

        <AttributionNotice actorName={actorName} />
      </div>
    </Modal>
  );
}

function RemoveFailure({ failure }: { failure: ApiError }) {
  const { t } = useI18n();

  if (failure.code === 'operator_not_nameable') {
    return (
      <Alert tone="deny" title={t('resource.notNameable.title')} live>
        <p>{t('resource.notNameable.body')}</p>
        <p style={{ marginTop: 8 }}>{t('resource.notNameable.fix')}</p>
      </Alert>
    );
  }
  if (failure.code === 'resource_not_found') {
    return (
      <Alert tone="neutral" title={t('resource.gone.title')} live>
        {t('resource.gone.body')}
      </Alert>
    );
  }
  return <ErrorAlert error={failure} />;
}
